import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { sortBy } from "lodash";

import { useUser } from "@components/user-context";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  type Placement,
} from "@floating-ui/react";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { useFuzzySearch } from "@hooks/use-fuzzy-search";
import { MetricFormatters } from "@north-beam/nb-client-side";
import { TextInput } from "@shared/text-input";

import { type DataColumn } from "../../types";

interface ChooserOption {
  id: string;
  columnName: string;
  label: string;
  disabled: boolean;
  iconLabel: string;
  children?: ChooserOption[];
}

type AppendSpendConstraintButtonProps = {
  onSelect: (id: string) => void;
  availableDataColumns: DataColumn[];
  selectedDataColumnsIds: string[];
  buttonText: string;
  placement?: Placement;
  previousPeriodSpendData: Record<string, number>;
};

export function GroupedDataColumnChooser({
  onSelect,
  availableDataColumns,
  selectedDataColumnsIds,
  buttonText,
  placement,
  previousPeriodSpendData,
}: AppendSpendConstraintButtonProps) {
  const spendDataColumnsOptions = useMemo(() => {
    const selectedDataColumnsIdsSet = new Set(selectedDataColumnsIds);
    const result: ChooserOption[] = [];

    availableDataColumns.forEach((dataColumn) => {
      const chooserOption = {
        id: String(dataColumn.id),
        columnName: dataColumn.column_name,
        label: dataColumn.friendly_name,
        disabled: selectedDataColumnsIdsSet.has(String(dataColumn.id)),
        iconLabel:
          dataColumn.mta_breakdowns?.[0].value ?? dataColumn.friendly_name,
      };

      result.push(chooserOption);
    });

    return result;
  }, [selectedDataColumnsIds, availableDataColumns]);

  const { user } = useUser();
  const { designElementManager } = useDesignElementManager();

  const [filteredOptions, { search, setSearch, clearSearch }] = useFuzzySearch(
    spendDataColumnsOptions,
    {
      searchKeys: ["label"],
    },
  );

  const listRef = useRef<(HTMLDivElement | null)[]>([]);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isMenuOpen,
    onOpenChange: setIsMenuOpen,
    transform: false,
    placement: placement ?? "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [flip(), shift(), offset(2)],
  });

  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    virtual: true,
    focusItemOnOpen: true,
    onNavigate: setActiveIndex,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [click, dismiss, listNavigation],
  );

  useEffect(() => {
    if (isMenuOpen) {
      clearSearch();
    }
  }, [isMenuOpen, clearSearch]);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();

      const selected = sortBy(filteredOptions, "label")[activeIndex ?? -1];

      if (selected && !selected.disabled) {
        onSelect(selected.id);
        setIsMenuOpen(false);
      }
    }
  };

  return (
    <div>
      <button
        className={clsx(
          "button bg-v2-primary border border-v2-tertiary text-v2-action hover:bg-v2-secondary sm font-semibold flex items-center gap-2 h-8 mt-4",
          {
            "hover:bg-v2-primary": !isMenuOpen,
            "text-white bg-cosmic border-cosmic": isMenuOpen,
          },
        )}
        ref={refs.setReference}
        {...getReferenceProps({
          onClick: (e) => e.preventDefault(),
          onKeyDown,
        })}
      >
        <i
          className={clsx("fa-regular fa-sharp fa-plus text-base", {
            "text-white": isMenuOpen,
          })}
        />
        {buttonText}
      </button>
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            transition={{ ease: "easeOut", duration: 0.2 }}
            initial={{ opacity: 0, translateY: "0.25rem" }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: "0.25rem" }}
            ref={refs.setFloating}
            style={floatingStyles}
            className="z-10 bg-v2-primary rounded-lg elevation-1 text-sm"
            {...getFloatingProps()}
          >
            <div className="px-4 pt-4 pb-3">
              <TextInput
                placeholder="Search..."
                rightIcon={
                  <i
                    className={clsx("fa-regular fa-sharp fa-magnifying-glass")}
                  />
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={onKeyDown}
              />
            </div>
            <div
              className={clsx(
                "min-h-[20rem] max-h-[20rem] min-w-[40rem] max-w-[40rem] overflow-auto",
              )}
            >
              {sortBy(filteredOptions, "label").map((item, index) => {
                const spend = previousPeriodSpendData[item.columnName] ?? 0;
                const icon = designElementManager
                  .getDesignElement(item.iconLabel)
                  .getComponent({ size: "20px" });

                return (
                  <div
                    key={item.id}
                    aria-disabled={item.disabled}
                    tabIndex={activeIndex === index ? 0 : -1}
                    ref={(node) => {
                      listRef.current[index] = node;
                    }}
                    className={clsx(
                      "flex items-center justify-between h-[40px] px-4",
                      {
                        "bg-cosmic-4 font-bold":
                          !item.disabled && activeIndex === index,
                        "cursor-pointer": !item.disabled,
                        "opacity-40": item.disabled,
                      },
                    )}
                    {...getItemProps({
                      onClick: () => {
                        if (!item.disabled) {
                          onSelect(item.id);
                          setIsMenuOpen(false);
                        }
                      },
                    })}
                  >
                    <div className="flex items-center gap-2 truncate">
                      {icon}
                      {item.label}
                    </div>
                    <div className="text-xxs text-right">
                      <div>
                        {MetricFormatters.currency(
                          spend,
                          user.currencies.currentCurrency,
                          { precision: "exact" },
                        )}
                      </div>
                      <div className="text-gray">Last period's spend</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
